/**
 * Built — Import & Position Register (UAT §4).
 *
 * Approved seats, who is in them, funding, and status — kept deliberately separate from the
 * demand calculation, so "what we've approved" is never confused with "what the maths says
 * we need". Increasing Approved Seats is the only way a vacancy is ever created, and it
 * happens automatically, always linked to that specific position row.
 *
 * Filled and vacant counts come from one shared allocation used everywhere — this table,
 * the KPI cards, Business Case, Hire/No-Hire and the CSV export all read the identical
 * numbers, which is what stops two screens quietly disagreeing about the same seat.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useModel } from '../state.jsx';
import { Icon } from '../components/IconSprite.jsx';
import { getAccessToken } from '../auth.jsx';

const money = (n) => '$' + Math.round(n).toLocaleString();

const FUNDING = {
  operating: ['Operating', 'var(--grn)'],
  capital: ['Capital', 'var(--blue)'],
  grant: ['Grant', 'var(--purple)'],
  'contractor-conversion': ['Contractor Conversion', 'var(--amber)'],
  frozen: ['Frozen', 'var(--ink4)'],
};

const STATUS = {
  active: ['Active', 'chip-green'],
  'pending-approval': ['Pending Approval', 'chip-amber'],
  frozen: ['Frozen', 'chip-grey'],
};

function Kpi({ label, value, sub, accent }) {
  return (
    <div className={`kpi ${accent ?? ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}

export function BuiltPositions() {
  const { state, engine, patch, isAdmin } = useModel();
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const allocations = useMemo(() => engine.getPlanAllocations(), [engine, state]);
  const deptName = (id) => state.depts.find((d) => d.id === id)?.name ?? id;
  const role = (id) => state.roles.find((r) => r.id === id);

  const totals = useMemo(() => {
    const rows = state.headcountPlan;
    return {
      approved: rows.reduce((s, p) => s + (p.count ?? 0), 0),
      filled: rows.reduce((s, p) => s + (allocations[p.id]?.filled ?? 0), 0),
      hireEligible: rows.reduce((s, p) => s + (engine.isHireEligible(p) ? (allocations[p.id]?.vacant ?? 0) : 0), 0),
      pending: rows.reduce((s, p) => s + (!engine.isHireEligible(p) ? (allocations[p.id]?.vacant ?? 0) : 0), 0),
      budget: rows.reduce((s, p) => s + (p.annualBudget ?? 0) * (p.count ?? 0), 0),
    };
  }, [state, engine, allocations]);

  // Grouped by department, as the legacy register is — a budget conversation happens one
  // department at a time.
  const groups = useMemo(() => {
    const map = new Map();
    for (const p of state.headcountPlan) {
      if (!map.has(p.dept)) map.set(p.dept, []);
      map.get(p.dept).push(p);
    }
    return [...map.entries()]
      .map(([dept, rows]) => ({
        dept,
        name: deptName(dept),
        rows,
        approved: rows.reduce((s, p) => s + (p.count ?? 0), 0),
        filled: rows.reduce((s, p) => s + (allocations[p.id]?.filled ?? 0), 0),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [state, allocations]);

  async function edit(id, changes) {
    setError(null);
    try { await patch('headcountPlan', id, changes); } catch (err) { setError(err.message); }
  }

  async function exportRegister() {
    setBusy(true);
    try {
      const token = await getAccessToken();
      const res = await fetch('/api/scenarios/live/export.xlsx', {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `CapaciTEA_Position_Register_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
          🏛 Built — Import &amp; Position Register
        </div>
        <button className="btn btn-secondary btn-sm" disabled={busy} onClick={exportRegister}>
          ⬇ Export Register
        </button>
        <Link to="/configure/import" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
          🏗 Import Built Export
        </Link>
      </div>

      {error && (
        <div className="alert" style={{ background: 'var(--ltred)', color: 'var(--red)', border: '1px solid var(--red)' }}>
          <span>⚠️</span><span>{error}</span>
        </div>
      )}

      <div className="card" style={{ background: '#eef0ff', border: '1px solid #4253e0' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 12 }}>
            <Icon name="ic-layers" style={{ width: 22, height: 22, color: '#4253e0', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#4253e0', marginBottom: 4 }}>
                Built is the single source of truth
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink2)', lineHeight: 1.6 }}>
                Because your version of Built holds both <strong>approved positions</strong> and{' '}
                <strong>current employees</strong>, one export gives CapaciTEA everything it needs —
                who is in each seat, what is vacant, funding source, budget code, and hire
                eligibility status. No separate HRIS import required.{' '}
                <Link to="/configure/import">Load a Built export →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="kpi-grid kpi-5">
        <Kpi label="Approved seats" value={totals.approved} sub="across all departments" />
        <Kpi label="Filled" value={totals.filled} sub="currently in roster" accent="green" />
        <Kpi
          label="Hire-eligible vacancies" value={totals.hireEligible}
          sub="funded & authorized to fill" accent={totals.hireEligible ? 'amber' : 'green'}
        />
        <Kpi
          label="Pending approval" value={totals.pending}
          sub="not yet authorized" accent={totals.pending ? 'amber' : 'green'}
        />
        <Kpi label="Total budget" value={`$${Math.round(totals.budget / 1000)}k`} sub="loaded annual cost" accent="blue" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '0 0 12px' }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink4)' }}>
          Funding:
        </span>
        {Object.entries(FUNDING).map(([key, [label, color]]) => (
          <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--ink3)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
            {label}
          </span>
        ))}
      </div>

      <div className="card">
        <div className="card-body flush">
          <div className="table-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Role / position</th>
                  <th>Budget code</th>
                  <th>Funding</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Approved seats</th>
                  <th style={{ textAlign: 'right' }}>Annual cost (per seat)</th>
                  <th style={{ textAlign: 'right' }}>Filled</th>
                  <th style={{ textAlign: 'right' }}>Vacant</th>
                  <th style={{ textAlign: 'center' }}>Gap vs. roster</th>
                  <th style={{ textAlign: 'center' }}>Hire eligible?</th>
                  <th>Needed by (manager-entered)</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <>
                    <tr key={g.dept} className="grp">
                      <td colSpan={13}>
                        ▶ {g.name} · {g.filled} filled / {g.approved} approved seats
                      </td>
                    </tr>
                    {g.rows.map((p) => {
                      const alloc = allocations[p.id] ?? { filled: 0, vacant: 0 };
                      const r = role(p.roleId);
                      const [fundLabel, fundColor] = FUNDING[p.funding] ?? [p.funding, 'var(--ink4)'];
                      const [statusLabel, statusCls] = STATUS[p.status] ?? [p.status, 'chip-grey'];
                      const eligible = engine.isHireEligible(p);
                      // Approved seats and real roster records should agree; when they don't,
                      // the register is describing seats nobody has been placed against.
                      const reconciles = alloc.filled + alloc.vacant === (p.count ?? 0);

                      return (
                        <tr key={p.id}>
                          <td style={{ fontSize: 11, color: 'var(--ink3)' }}>{g.name}</td>
                          <td>
                            <strong>{r?.name ?? p.roleId}</strong>
                            {p.note && <div style={{ fontSize: 10, color: 'var(--ink4)' }}>{p.note}</div>}
                          </td>
                          <td className="num-mono" style={{ fontSize: 11 }}>{p.budgetCode ?? '—'}</td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: fundColor }} />
                              {fundLabel}
                            </span>
                          </td>
                          <td>
                            {isAdmin ? (
                              <select className="ei" value={p.status} style={{ minWidth: 132 }}
                                onChange={(e) => edit(p.id, { status: e.target.value })}>
                                {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
                              </select>
                            ) : <span className={`chip ${statusCls}`}>{statusLabel}</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {isAdmin ? (
                              <input className="ei num" type="number" defaultValue={p.count} style={{ width: 52 }}
                                onBlur={(e) => edit(p.id, { count: Number(e.target.value) })} />
                            ) : <span className="num-mono">{p.count}</span>}
                          </td>
                          <td style={{ textAlign: 'right' }} className="num-mono">{money(p.annualBudget ?? 0)}</td>
                          <td className="num-mono" style={{ textAlign: 'right', color: 'var(--grn)', fontWeight: 600 }}>
                            {alloc.filled}
                          </td>
                          <td style={{ textAlign: 'right', color: alloc.vacant ? 'var(--amber)' : 'var(--ink4)', fontWeight: 600 }} className="num-mono">
                            {alloc.vacant}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {reconciles
                              ? <span style={{ color: 'var(--grn)' }}>✓</span>
                              : <span style={{ color: 'var(--amber)' }} title="Approved seats do not reconcile against roster records">⚠</span>}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {alloc.vacant === 0
                              ? <span style={{ color: 'var(--ink4)' }}>—</span>
                              : eligible
                                ? <span className="chip chip-green">Yes</span>
                                : <span className="chip chip-grey">No</span>}
                          </td>
                          <td>
                            {isAdmin ? (
                              <input className="ei" type="month" defaultValue={p.neededBy ?? ''}
                                onBlur={(e) => edit(p.id, { neededBy: e.target.value })} />
                            ) : <span className="num-mono">{p.neededBy ?? '—'}</span>}
                          </td>
                          <td style={{ fontSize: 11, color: 'var(--ink3)' }}>{p.note ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="page-note">
        Increasing Approved Seats adds a vacant roster record for that role and department, so
        Open Reqs rises immediately and Business Case gains a seat to point at. It does
        <strong> not</strong> change the department's volume gap or support ratio at all —
        productive FTE excludes vacant records and implied FTE comes entirely from the volume
        driver — so the gap is unaffected until a real person fills the seat. Setting a position
        to Pending Approval or Frozen makes it hire-ineligible, which can flip Business Case from
        Review: Hire Recommended to Position Request Recommended. Needed-By is manually entered
        and feeds the forecast strip and the export only — it affects no KPI, status or verdict.
      </p>
    </>
  );
}
